/**
 * Financeiro › Painel › Resumo: indicadores do período, caixa e resultado, futuro,
 * saldo projetado (com impostos e repasses previstos) e faturamento por unidade.
 */
import { useState, type ReactNode } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { api, qs } from "@/lib/api";
import { navigate } from "@/lib/router";
import { fmtMoney } from "@/lib/format";
import { useLookups } from "@/lib/catalog";
import { useUnitStore } from "@/lib/unit";
import { ErrorBox, Loading } from "@/ui/kit";
import { PeriodPicker, usePeriod } from "@/ui/period";
import { AREA_COLORS } from "./SocioView";

interface PanelData {
  from: string; to: string; today: string; nextDay: string; unitId: string | null;
  quick: {
    billedDue: { amount: string; count: number };
    billedComp: { amount: string; count: number; diff: string };
    received: { amount: string; count: number; pctOfDue: number };
    toReceive: { amount: string; count: number; pct: number };
    overduePeriod: { amount: string; count: number; pct: number };
    overdueAll: { amount: string; count: number };
  };
  result: {
    cashNow: string; accounts: number; cashGenerated: string; cashGeneratedBasis: string;
    revenue: string; deductions: string; netRevenue: string; opCost: string; opCostParts: { deducao: string; repasses: string; custo: string; despesa: string };
    operating: string; margin: number; withdrawals: string | null; investments: string; final: string;
    ebitda?: string; ebit?: string; financial?: string; net?: string;
  };
  future: {
    receivable: string; receivableCount: number; payable: string; payableCount: number; obligations: string; obligationsCount: number;
    cards?: string; cardsCount?: number;
    projection: {
      horizon: string; horizonLabel: string; until: string; includeOverdue: boolean;
      cash: string; receivable: string; payable: string; obligations: string; partnerForecast: string;
      tax: { amount: string; rate: number; rateSource: "configurada" | "media_12m" | "sem_base"; base: string; baseMonthReceived: string; launched: string };
      balance: string;
    };
    horizons: Array<{ key: string; label: string }>;
  };
  byUnit: {
    rows: Array<{ id: string; name: string; billedDue: string; billedComp: string; received: string; receivable: string; overdue: string; pctBilledDue: number; pctBilledComp: number; pctReceived: number }>;
    totals: { billedDue: string; billedComp: string; received: string; receivable: string; overdue: string };
  };
}

type Tone = "blue" | "violet" | "green" | "amber" | "orange" | "red" | "navy" | "gray";

function Metric({ label, value, hint, tone = "navy", onClick, big, valueTone }: {
  label: string; value: string; hint?: ReactNode; tone?: Tone; onClick?: () => void; big?: boolean; valueTone?: "good" | "bad" | "warn";
}): JSX.Element {
  return (
    <button type="button" className={`fm tone-${tone} ${big ? "big" : ""} ${onClick ? "" : "static"}`} onClick={onClick}>
      <span className="fm-l">{label}</span>
      <span className={`fm-v ${valueTone ?? ""}`}>{value}</span>
      {hint && <span className="fm-h">{hint}</span>}
    </button>
  );
}

const pctTxt = (n: number) => `${n.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;
const sign = (v: string) => (Number(v) < 0 ? "bad" : Number(v) > 0 ? "good" : undefined);

/** Em Indicadores › Financeiro o período e a unidade vêm dos filtros da página (props); sozinho, tem os próprios. */
export function FinancePanel({ from: fromP, to: toP, unitId: unitP }: { from?: string; to?: string; unitId?: string } = {}): JSX.Element {
  const [ownPeriod, setPeriod] = usePeriod("mes");
  const storeUnit = useUnitStore((s) => s.unitId);
  const setUnit = useUnitStore((s) => s.set);
  const external = !!(fromP && toP);
  const period = external ? { ...ownPeriod, from: fromP!, to: toP! } : ownPeriod;
  const unit = unitP ?? storeUnit;
  const [horizon, setHorizon] = useState("mes");
  const [until, setUntil] = useState("");
  const [withOverdue, setWithOverdue] = useState(false);
  const { catalog, unitName } = useLookups();
  const { data, error, isLoading } = useQuery({
    queryKey: ["fin-painel", period.from, period.to, unit, horizon, until, withOverdue],
    queryFn: () => api.get<PanelData>(`/financeiro/painel${qs({ from: period.from, to: period.to, unitId: unit || undefined, horizonte: horizon, ate: horizon === "data" && until ? until : undefined, incluirVencidos: withOverdue ? "1" : undefined })}`),
    placeholderData: keepPreviousData,
  });
  const units = (catalog?.units ?? []).filter((u) => u.active);
  if (error) return <ErrorBox error={error} />;
  if (isLoading || !data) return <Loading lines={8} />;
  const q = data.quick, r = data.result, f = data.future, p = f.projection;
  const go = (aba: string) => navigate(`/financeiro?aba=${aba}`);
  const rateTxt = p.tax.rateSource === "sem_base" ? "sem alíquota (configure em Cadastros)" : `${pctTxt(p.tax.rate * 100)} ${p.tax.rateSource === "configurada" ? "(configurada)" : "(média real dos últimos 12 meses)"}`;

  return (
    <div className="stack gap-16">
      {!external && <div className="toolbar">
        <PeriodPicker value={period} onChange={setPeriod} />
        {unitP === undefined && units.length > 1 && (
          <select className="input" style={{ width: "auto" }} value={storeUnit} onChange={(e) => setUnit(e.target.value)} title="Mesma unidade do menu lateral">
            <option value="">Todas as unidades (consolidado)</option>
            {units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        )}
        <span className="spacer" />
        <span className="muted small">{data.unitId ? `Unidade: ${unitName(data.unitId)}` : "Consolidado"}</span>
      </div>}

      <section className="card">
        <div className="card-head"><h2>Resumo rápido</h2><span className="muted small">{data.from.split("-").reverse().join("/")} a {data.to.split("-").reverse().join("/")}</span></div>
        <div className="fm-grid">
          <Metric tone="navy" label="Faturamento (vencimento)" value={fmtMoney(q.billedDue.amount)} hint={`${q.billedDue.count} parcela(s) vencendo no período`} onClick={() => go("titulos")} />
          <Metric tone="violet" label="Faturamento (competência)" value={fmtMoney(q.billedComp.amount)}
            hint={`${q.billedComp.count} lanç. · ${Number(q.billedComp.diff) >= 0 ? "+" : ""}${fmtMoney(q.billedComp.diff)} vs. vencimento`} onClick={() => go("titulos")} />
          <Metric tone="green" label="Total recebido" value={fmtMoney(q.received.amount)} valueTone="good"
            hint={`${q.received.count} baixa(s) · ${pctTxt(q.received.pctOfDue)} do faturado no período`} onClick={() => go("fluxo")} />
          <Metric tone="amber" label="A receber (do período)" value={fmtMoney(q.toReceive.amount)} valueTone="warn"
            hint={`${q.toReceive.count} no prazo · ${pctTxt(q.toReceive.pct)} do faturado`} onClick={() => go("emaberto")} />
          <Metric tone="orange" label="Inadimplência do período" value={fmtMoney(q.overduePeriod.amount)} valueTone={Number(q.overduePeriod.amount) > 0 ? "bad" : undefined}
            hint={`${q.overduePeriod.count} vencida(s) · ${pctTxt(q.overduePeriod.pct)} do faturado`} onClick={() => go("emaberto")} />
          <Metric tone="red" label="Inadimplência (todo o histórico)" value={fmtMoney(q.overdueAll.amount)} valueTone={Number(q.overdueAll.amount) > 0 ? "bad" : undefined}
            hint={`${q.overdueAll.count} vencida(s) não paga(s)`} onClick={() => go("emaberto")} />
        </div>
      </section>

      <section className="card">
        <div className="card-head"><h2>Caixa e resultado do período</h2><span className="muted small">regime de caixa (o que entrou e saiu)</span></div>
        <div className="fm-grid">
          <Metric tone="navy" big label="Caixa atual" value={fmtMoney(r.cashNow)} valueTone={sign(r.cashNow)} hint={`saldo de ${r.accounts} conta(s) hoje`} onClick={() => go("contas")} />
          <Metric tone="green" label="Caixa gerado no período" value={fmtMoney(r.cashGenerated)} valueTone={sign(r.cashGenerated)} hint={r.cashGeneratedBasis} onClick={() => go("fluxo")} />
          <Metric tone="blue" label="Receita recebida" value={fmtMoney(r.revenue)} hint="receitas baixadas no período" onClick={() => go("dre")} />
          <Metric tone="orange" label="Deduções e tributos" value={fmtMoney(r.deductions)}
            hint={`repasses ${fmtMoney(r.opCostParts.repasses)} + tributos/outras ${fmtMoney(r.opCostParts.deducao)} · receita líquida ${fmtMoney(r.netRevenue)}`} onClick={() => go("dre")} />
          <Metric tone="red" label="Custos e despesas operacionais" value={fmtMoney(r.opCost)}
            hint={`despesas ${fmtMoney(r.opCostParts.despesa)} + custos diretos ${fmtMoney(r.opCostParts.custo)}`} onClick={() => go("dre")} />
          <Metric tone="green" label="EBITDA" value={fmtMoney(r.ebitda ?? r.operating)} valueTone={sign(r.ebitda ?? r.operating)} hint="receita − deduções − tributos − custos − despesas operacionais" onClick={() => go("dre")} />
          <Metric tone="green" label="Margem EBITDA" value={pctTxt(r.margin)} valueTone={r.margin < 0 ? "bad" : "good"} hint="EBITDA ÷ receita recebida" />
          <Metric tone="blue" label="Resultado líquido" value={fmtMoney(r.net ?? r.final)} valueTone={sign(r.net ?? r.final)} hint={`EBIT ${fmtMoney(r.ebit ?? "0")} · financeiro ${fmtMoney(r.financial ?? "0")}`} onClick={() => go("dre")} />
          {r.withdrawals !== null && <Metric tone="violet" label="Distribuição" value={fmtMoney(r.withdrawals)} hint={Number(r.investments) ? `CAPEX ${fmtMoney(r.investments)} (fora do resultado)` : "no período"} onClick={() => go("distribuicoes")} />}
          <Metric tone="navy" label={r.withdrawals !== null ? "Resultado retido" : "Resultado do período"} value={fmtMoney(r.final)} valueTone={sign(r.final)} hint={r.withdrawals !== null ? "resultado líquido − distribuição" : "resultado líquido"} onClick={() => go("dre")} />
        </div>
      </section>

      <div className="grid side">
        <section className="card">
          <div className="card-head"><h2>Futuro</h2></div>
          <div className="fm-grid cols-3">
            <Metric tone="green" label="A receber (futuro)" value={fmtMoney(f.receivable)} hint={`${f.receivableCount} parcela(s) a vencer`} onClick={() => go("emaberto")} />
            <Metric tone="red" label="A pagar (futuro)" value={fmtMoney(f.payable)} hint={`${f.payableCount} parcela(s) a vencer`} onClick={() => go("titulos")} />
            {f.cards !== undefined && <Metric tone="orange" label="Cartões a pagar" value={fmtMoney(f.cards)} hint={`${f.cardsCount ?? 0} parcela(s) em faturas`} onClick={() => go("contas")} />}
            <Metric tone="violet" label="Repasses pendentes" value={fmtMoney(f.obligations)} hint={`${f.obligationsCount} a pagar a parceiros`} onClick={() => go("repasses")} />
          </div>
          <UnitSplit data={data} selected={unit} onPick={(id) => { if (unitP === undefined) setUnit(id === unit ? "" : id); }} />
        </section>

        <section className="card proj-card">
          <div className="card-head">
            <h2>Saldo projetado</h2>
            <div className="row nowrap">
              <select className="input" style={{ width: "auto" }} value={horizon} onChange={(e) => setHorizon(e.target.value)}>
                {f.horizons.map((h) => <option key={h.key} value={h.key}>{h.label}</option>)}
              </select>
              {horizon === "data" && <input type="date" className="input" style={{ width: "auto" }} min={data.nextDay} value={until} onChange={(e) => setUntil(e.target.value)} />}
            </div>
          </div>
          <div className={`proj-total ${Number(p.balance) < 0 ? "bad" : "good"}`}>{fmtMoney(p.balance)}</div>
          <div className="muted small" style={{ marginBottom: 10 }}>até {p.until.split("-").reverse().join("/")}</div>
          <table className="proj-table">
            <tbody>
              <tr><td>Caixa atual</td><td>{fmtMoney(p.cash)}</td></tr>
              <tr><td>(+) A receber no horizonte{p.includeOverdue ? " (inclui vencidos)" : ""}</td><td className="text-ok">{fmtMoney(p.receivable)}</td></tr>
              <tr><td>(−) A pagar até o horizonte</td><td className="text-bad">{fmtMoney(-Number(p.payable))}</td></tr>
              <tr><td>(−) Repasses pendentes</td><td className="text-bad">{fmtMoney(-Number(p.obligations))}</td></tr>
              <tr><td>(−) Repasses previstos sobre o que vai entrar</td><td className="text-bad">{fmtMoney(-Number(p.partnerForecast))}</td></tr>
              <tr><td title={`Base: a receber no horizonte + recebido no mês (${fmtMoney(p.tax.baseMonthReceived)}). Já lançados: ${fmtMoney(p.tax.launched)}.`}>(−) Impostos previstos <span className="muted small">· {rateTxt}</span></td><td className="text-bad">{fmtMoney(-Number(p.tax.amount))}</td></tr>
              <tr className="strong"><td>Saldo projetado</td><td>{fmtMoney(p.balance)}</td></tr>
            </tbody>
          </table>
          <div className="muted small" style={{ marginTop: 8 }}>
            Impostos previstos = alíquota × (a receber no horizonte + recebido no mês corrente) − impostos já lançados para o mês corrente em diante.
            {p.tax.rateSource !== "configurada" && <> Defina a alíquota em <button className="link-btn small" onClick={() => go("cadastros")}>Cadastros › encargos</button>.</>}
          </div>
          <label className="check" style={{ marginTop: 8 }}><input type="checkbox" checked={withOverdue} onChange={(e) => setWithOverdue(e.target.checked)} /> Contar a inadimplência como recebível</label>
        </section>
      </div>
    </div>
  );
}

function UnitSplit({ data, selected, onPick }: { data: PanelData; selected: string; onPick: (id: string) => void }): JSX.Element | null {
  const rows = data.byUnit.rows;
  const [metric, setMetric] = useState<"billedComp" | "billedDue" | "received">("billedComp");
  if (rows.length < 2) return null;
  const pctKey = metric === "billedComp" ? "pctBilledComp" : metric === "billedDue" ? "pctBilledDue" : "pctReceived";
  const total = data.byUnit.totals[metric];
  return (
    <div style={{ marginTop: 18 }}>
      <div className="card-head" style={{ marginBottom: 8 }}>
        <h2>Faturamento por unidade</h2>
        <div className="seg">
          <button className={metric === "billedComp" ? "active" : ""} onClick={() => setMetric("billedComp")}>Competência</button>
          <button className={metric === "billedDue" ? "active" : ""} onClick={() => setMetric("billedDue")}>Vencimento</button>
          <button className={metric === "received" ? "active" : ""} onClick={() => setMetric("received")}>Recebido</button>
        </div>
      </div>
      <div className="split-bar">
        {rows.map((u, i) => u[pctKey] > 0 && (
          <span key={u.id} style={{ width: `${u[pctKey]}%`, background: AREA_COLORS[i % AREA_COLORS.length] }} title={`${u.name}: ${fmtMoney(u[metric])} (${pctTxt(u[pctKey])})`} />
        ))}
      </div>
      <div className="table-wrap">
        <table className="table compact">
          <thead><tr><th>Unidade</th><th className="right">Valor</th><th className="right">%</th><th className="right">Recebido</th><th className="right">A receber</th><th className="right">Inadimplência</th></tr></thead>
          <tbody>
            {rows.map((u, i) => (
              <tr key={u.id} className={`clickable ${selected === u.id ? "row-sel" : ""}`} onClick={() => onPick(u.id)} title="Clique para ver só esta unidade (de novo para voltar ao consolidado)">
                <td><i className="dot-sq" style={{ background: AREA_COLORS[i % AREA_COLORS.length] }} />{u.name}</td>
                <td className="right">{fmtMoney(u[metric])}</td>
                <td className="right"><b>{pctTxt(u[pctKey])}</b></td>
                <td className="right">{fmtMoney(u.received)}</td>
                <td className="right">{fmtMoney(u.receivable)}</td>
                <td className={`right ${Number(u.overdue) > 0 ? "text-bad" : ""}`}>{fmtMoney(u.overdue)}</td>
              </tr>
            ))}
            <tr className="strong-row"><td>Total</td><td className="right">{fmtMoney(total)}</td><td className="right">100%</td><td className="right">{fmtMoney(data.byUnit.totals.received)}</td><td className="right">{fmtMoney(data.byUnit.totals.receivable)}</td><td className="right">{fmtMoney(data.byUnit.totals.overdue)}</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
